import { EMAIL_TEMPLATE_VARIABLE_CATEGORIES, EMAIL_TEMPLATE_VARIABLES } from '../constants/emailTemplateVariables'

const ALLOWED_TAGS = 'a, b, blockquote, br, div, em, h1-h6, hr, i, img, li, ol, p, s, span, strong, table, tbody, td, tfoot, th, thead, tr, u, ul'
const ALLOWED_STYLES =
    'colors and inert gradients, all border and border-radius variants, shadows, typography, dimensions, margin and padding, alignment, lists, and tables'

export type TemplateAiAssistant = 'chatgpt' | 'claude'

/** The editor button the prompt sends the user back to. One constant for both, so a rename cannot leave the
 *  prompt pointing at a button that no longer exists. */
export const PASTE_AI_RESULT_LABEL = 'Paste AI result'

const ASSISTANT_URLS: Record<TemplateAiAssistant, { baseUrl: string; promptParameter: string }> = {
    chatgpt: { baseUrl: 'https://chatgpt.com/', promptParameter: 'prompt' },
    claude: { baseUrl: 'https://claude.ai/new', promptParameter: 'q' },
}

/** Every field with its meaning, grouped under whose data it is. Derived from the field list, so a new field
 *  reaches the assistant with the meaning it was defined with — see emailTemplateVariables.ts. */
const describeVariables = (): string =>
    EMAIL_TEMPLATE_VARIABLE_CATEGORIES.map(({ category, meaning }) =>
        [
            `${category} — ${meaning}:`,
            ...EMAIL_TEMPLATE_VARIABLES.filter((variable) => variable.category === category).map(({ key, meaning }) => `- {{${key}}}: ${meaning}`),
        ].join('\n')
    ).join('\n\n')

export const buildTemplateAiPrompt = (subject: string, html: string): string => {
    return `HOW TO USE THIS PROMPT:
1. Go to the bottom of this message.
2. Replace the placeholder under "REQUESTED CHANGE" with what you want changed.
3. Send the message.
4. Copy your whole answer, go back to the template editor, click "Edit with AI", then "${PASTE_AI_RESULT_LABEL}".
5. Check the preview it shows, then click "Save template".

You are editing an HTML email template for a freight dispatcher.

Modify only the template HTML and return ONLY the final raw HTML without Markdown fences or explanation.

Security and compatibility rules:
- Never add JavaScript, script tags, event handlers, iframes, forms, SVG, MathML, embedded objects, external stylesheets, or CSS url()/image-set()/expression()/@import.
- Use only these HTML tags: ${ALLOWED_TAGS}.
- Use inline styles only. Allowed CSS categories: ${ALLOWED_STYLES}.
- Do not use positioning/inset/z-index, pointer-events, cursor, transforms, filters, transitions, animations, or generated content.
- For columns or anything side by side (label/value rows, a button beside text), use <table role="presentation"> with inline-styled cells. Never use flexbox, grid or CSS columns: Outlook ignores them and the layout falls apart.
- Keep every {{variable}} token intact unless the requested change explicitly removes that content.
- Do not replace variables with example values.
- Every load, route, price, broker, driver, dispatcher or company detail you add must be one of the supported variables below — never a literal value, even if the request or the current HTML shows one (no sample names, cities, amounts, miles, MC/USDOT numbers, phones, emails or addresses).
- Choose variables by their meaning. Broker variables describe the brokerage receiving this email; Company and Dispatcher variables describe the sender. Never use a Broker variable for the sender's own details.
- Use only the supported variables: an unknown {{token}} is rejected when the template is saved. If a requested detail has no variable, leave it out.
- Keep links limited to http, https, mailto, or tel. Images must use https or cid.
- Preserve a responsive email-safe layout suitable for Gmail and Outlook.

Supported variables, grouped by whose data they are:

${describeVariables()}

Current subject (context only; do not include it in the returned HTML):
${subject}

Current template HTML begins below:
<template_html>
${html}
</template_html>

REQUESTED CHANGE — WRITE WHAT YOU WANT HERE BEFORE SENDING:
<requested_change>
[Delete this placeholder and describe the change. Example: Make the offer amount green and add our company name above the contact details.]
</requested_change>`
}

export const buildTemplateAiAssistantUrl = (assistant: TemplateAiAssistant, prompt: string): string => {
    const { baseUrl, promptParameter } = ASSISTANT_URLS[assistant]
    const url = new URL(baseUrl)
    url.searchParams.set(promptParameter, prompt)
    return url.toString()
}
